import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Grid,
  Paper,
  Typography,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Select,
  MenuItem,
  Tooltip,
  Checkbox,
  styled,
} from '@mui/material';
import { PrimaryButton, ConfirmDeleteDialog } from '../components';
import InfoHeader from '../components/InfoHeader';
import TableCell, { tableCellClasses } from '@mui/material/TableCell';
import { AmbienteInternoPontoFraco } from '../types/negocio/AmbienteInternoPontoFraco';
import { ambienteInternoPontoFracoService } from '../services/AmbienteInternoPontoFracoService';
import toastService from '../utils/toastService';
import SaveStatusIndicator, { SaveStatus } from '../components/SaveStatusIndicator';
import LoadingIndicator from '../components/LoadingIndicator';
import InfoPopover from '../components/InfoPopover';
import ProtecaoBotao from '../components/ProtecaoBotao';
import { useAcesso } from '../hooks/useAcesso';
import ProtectedBasicMenu from '../components/ProtectedBasicMenu';

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: '#042E4B',
    color: theme.palette.common.white,
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '&:nth-of-type(odd)': {
    backgroundColor: theme.palette.action.hover,
  },
  '&:last-child td, &:last-child th': {
    border: 0,
  },
}));

const gravidadeOptions = [
  { value: 4, label: "(4) Alta" },
  { value: 3, label: "(3) Média" },
  { value: 2, label: "(2) Baixa" },
  { value: 1, label: "(1) Nenhuma" },
];

const urgenciaOptions = [
  { value: 4, label: "(4) Alta" },
  { value: 3, label: "(3) Média" },
  { value: 2, label: "(2) Baixa" },
  { value: 1, label: "(1) Nenhuma" },
];

const tendenciaOptions = [
  { value: 4, label: "(4) Ampliar" },
  { value: 3, label: "(3) Estabilizar" },
  { value: 2, label: "(2) Reduzir" },
  { value: 1, label: "(1) Zerar" },
];

const PontosFracos: React.FC = () => {
  const [rows, setRows] = useState<AmbienteInternoPontoFraco[]>([]);
  const [editingRow, setEditingRow] = useState<number | null>(null);
  const [saveStatus, setSaveStatus] = useState<SaveStatus>('idle');
  const [loading, setLoading] = useState<boolean>(true);
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
  const [selectedGuids, setSelectedGuids] = useState<string[]>([]);
  const [allSelected, setAllSelected] = useState(false);
  const rowRefs = useRef<(HTMLTableRowElement | null)[]>([]);
  const { temAcesso } = useAcesso();
  const originalValueRef = useRef<string>('');
  const canBlurRef = useRef<boolean>(false);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const data = await ambienteInternoPontoFracoService.listarTodos();
        setRows(data);
      } catch (error) {
        console.error('Error fetching data:', error);
        toastService.error('Erro ao carregar pontos fracos');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const handleChange = async (index: number, field: string, value: string | number, save: boolean = true) => {
    const updatedRows = [...rows];
    (updatedRows[index] as any)[field] = value;
    updatedRows[index].pontuacao = updatedRows[index].gravidade * updatedRows[index].urgencia * updatedRows[index].tendencia;
    if (save) {
      const saved = await salvar(updatedRows[index]);
      if (saved && saved.guid && !updatedRows[index].guid) {
        updatedRows[index].guid = saved.guid;
      }
    }
    setRows(updatedRows);
  };

  const toggleSelect = (guid?: string) => {
    if (!guid) return;
    setSelectedGuids((prev) => (prev.includes(guid) ? prev.filter((g) => g !== guid) : [...prev, guid]));
  };

  const handleSelectAll = () => {
    if (allSelected) {
      setSelectedGuids([]);
      setAllSelected(false);
    } else {
      const guids = rows.map((r) => r.guid).filter((g): g is string => !!g);
      setSelectedGuids(guids);
      setAllSelected(true);
    }
  };

  useEffect(() => {
    const selectableCount = rows.filter((r) => !!r.guid).length;
    setAllSelected(selectableCount > 0 && selectedGuids.length === selectableCount);
  }, [rows, selectedGuids]);

  const handleAddRow = async () => {
    const novo: AmbienteInternoPontoFraco = {
      descricao: '',
      gravidade: 1,
      urgencia: 1,
      tendencia: 1,
      pontuacao: 1,
      acoes: '',
      ordem: rows.length + 1,
    };
    const newRows = [...rows, novo];
    setRows(newRows);

    const newIndex = newRows.length - 1;
    originalValueRef.current = '';
    canBlurRef.current = false;
    setEditingRow(newIndex);

    setTimeout(() => {
      canBlurRef.current = true;
      const el = rowRefs.current[newIndex];
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 300);
  };

  async function salvar(ambienteInternoPontoForte: AmbienteInternoPontoFraco): Promise<AmbienteInternoPontoFraco | null> {
    setSaveStatus('saving');
    try {
      const saved = await ambienteInternoPontoFracoService.save(ambienteInternoPontoForte);
      setSaveStatus('saved');
      setTimeout(() => setSaveStatus('idle'), 3000);
      return saved;
    } catch (error) {
      console.error('Error saving row:', error);
      setSaveStatus('error');
      toastService.error('Erro ao salvar ponto fraco');
      return null;
    }
  }

  const handleDeleteRow = async (index: number) => {
    setSaveStatus('saving');
    try {
      if (rows[index].guid) await ambienteInternoPontoFracoService.deletar(rows[index].guid!);
      const newRows = [...rows];
      newRows.splice(index, 1);
      newRows.forEach((row, i) => (row.ordem = i + 1));
      await ambienteInternoPontoFracoService.salvarTodos(newRows);
      setRows(newRows);
      setSaveStatus('saved');
      setTimeout(() => setSaveStatus('idle'), 3000);
    } catch (error) {
      console.error('Error deleting row:', error);
      setSaveStatus('error');
      toastService.error('Erro ao deletar ponto fraco');
    }
  };

  const handleDeleteAll = async () => {
    setOpenConfirmDialog(false);
    setSaveStatus('saving');
    try {
      const guidsToDelete = selectedGuids;
      if (guidsToDelete.length > 0) {
        await ambienteInternoPontoFracoService.deletarEmMassa(guidsToDelete);
      }
      const remaining = rows.filter((r) => !(r.guid && guidsToDelete.includes(r.guid)));
      setRows(remaining);
      setSelectedGuids([]);
      setSaveStatus('saved');
      setTimeout(() => setSaveStatus('idle'), 3000);
    } catch (error) {
      console.error('Error deleting all rows:', error);
      setSaveStatus('error');
      toastService.error('Erro ao excluir todos os Pontos Fracos');
    }
  };

  return (
    <Box>
      <InfoHeader
        title="Pontos Fracos"
        message="Avalie os pontos fracos de sua empresa. Classifique gravidade, urgência e tendência para cada item."
        isImportant
        url='https://youtu.be/VuCNhlNEy2Q'
        modalTitle='Entenda a aba Ambiente Interno'
      />

      <ProtecaoBotao tela="ambiente-interno" tipo="alteracao">
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'end', gap: 2 }}>
            <Tooltip title="Adicionar um novo Ponto Fraco">
              <PrimaryButton onClick={handleAddRow}>
                + Adicionar
              </PrimaryButton>
            </Tooltip>
            <Tooltip title={selectedGuids.length > 0 ? 'Excluir' : 'Selecione ao menos um item'}>
              <div>
                <PrimaryButton
                  variant="outlined"
                  color="error"
                  disabled={selectedGuids.length === 0}
                  onClick={() => setOpenConfirmDialog(true)}
                >
                  Excluir
                </PrimaryButton>
              </div>
            </Tooltip>
          </Box>
          <SaveStatusIndicator status={saveStatus} />
        </Box>
      </ProtecaoBotao>

      <Grid container sx={{ mt: 2 }}>
        <Grid size={{ xs: 12 }}>
          <Paper elevation={3}>
            <TableContainer style={{ borderRadius: '8px' }}>
              <Table sx={{ minWidth: 700 }}>
                <TableHead>
                  <TableRow>
                    <StyledTableCell align="center" sx={{ width: '40px', paddingLeft: '8px', paddingRight: '8px' }}>
                      <Checkbox
                        checked={allSelected}
                        indeterminate={selectedGuids.length > 0 && !allSelected}
                        onChange={handleSelectAll}
                        inputProps={{ 'aria-label': 'select all' }}
                        sx={{ color: 'white', '&.Mui-checked': { color: 'white' }, '&.MuiCheckbox-indeterminate': { color: 'white' } }}
                      />
                    </StyledTableCell>
                    <StyledTableCell width="54%"><strong>Ponto Fraco</strong></StyledTableCell>
                    <StyledTableCell align="center" width="12%">
                      <InfoPopover title="Gravidade" description="Consequência caso não resolvido." placement="top"><strong>Gravidade</strong></InfoPopover>
                    </StyledTableCell>
                    <StyledTableCell align="center" width="12%">
                      <InfoPopover title="Urgência" description="Necessidade de tempo para resolução." placement="top"><strong>Urgência</strong></InfoPopover>
                    </StyledTableCell>
                    <StyledTableCell align="center" width="12%">
                      <InfoPopover title="Tendência" description="Evolução ao longo do tempo." placement="top"><strong>Tendência</strong></InfoPopover>
                    </StyledTableCell>
                    <StyledTableCell align="center" width="5%"><strong>Pontuação</strong></StyledTableCell>
                    <StyledTableCell align="center" width="5%"><strong>Ações</strong></StyledTableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    <TableRow><TableCell colSpan={7}><LoadingIndicator message="Carregando Pontos Fracos..." /></TableCell></TableRow>
                  ) : rows.length === 0 ? (
                    <TableRow><TableCell colSpan={7} sx={{ textAlign: 'center', py: 4 }}><Typography>Nenhum ponto fraco encontrado.</Typography></TableCell></TableRow>
                  ) : rows.map((row, index) => (
                    <StyledTableRow key={index} ref={(el) => { rowRefs.current[index] = el }}>
                      <StyledTableCell align="center">
                        <Checkbox
                          checked={!!row.guid && selectedGuids.includes(row.guid)}
                          disabled={!row.guid}
                          onChange={() => row.guid && toggleSelect(row.guid)}
                          inputProps={{ 'aria-label': `select row ${index}` }}
                        />
                      </StyledTableCell>
                      <StyledTableCell>
                        {editingRow === index ? (
                          <>
                            <TextField
                              fullWidth
                              multiline
                              minRows={1}
                              variant="standard"
                              inputRef={(input) => {
                                if (input) {
                                  setTimeout(() => {
                                    input.focus();
                                  }, 100);
                                }
                              }}
                              value={row.descricao}
                              onFocus={() => {
                                originalValueRef.current = row.descricao;
                                canBlurRef.current = false;
                                setTimeout(() => {
                                  canBlurRef.current = true;
                                }, 200);
                              }}
                              onChange={(e) => handleChange(index, 'descricao', e.target.value, false)}
                              onKeyDown={async (e) => {
                                if (e.key === 'Enter') {
                                  e.preventDefault();
                                  if (row.descricao.trim() !== '') {
                                    const saved = await salvar(row);
                                    if (saved && saved.guid && !row.guid) {
                                      const updatedRows = [...rows];
                                      updatedRows[index].guid = saved.guid;
                                      setRows(updatedRows);
                                    }
                                    setEditingRow(null);
                                  } else {
                                    toastService.warning("A descrição não pode estar vazia.");
                                  }
                                }
                              }}
                              onBlur={async () => {
                                if (!canBlurRef.current) return;
                                
                                if (row.descricao.trim() !== '') {
                                  if (row.descricao !== originalValueRef.current) {
                                    const saved = await salvar(row);
                                    if (saved && saved.guid && !row.guid) {
                                      const updatedRows = [...rows];
                                      updatedRows[index].guid = saved.guid;
                                      setRows(updatedRows);
                                    }
                                  }
                                  setEditingRow(null);
                                } else {
                                  if (originalValueRef.current === '') {
                                    toastService.warning("A descrição não pode estar vazia.");
                                    const newRows = [...rows];
                                    newRows.splice(index, 1);
                                    setRows(newRows);
                                  } else {
                                    handleChange(index, 'descricao', originalValueRef.current, false);
                                  }
                                  setEditingRow(null);
                                }
                              }}
                              sx={{ '& .MuiInputBase-input': { fontSize: 16, cursor: 'text', padding: '4px 0px' } }}
                            />
                            <Typography sx={{ color: 'gray', fontSize: '0.75rem', mt: 1 }}>
                              Pressione "Enter" ou clique fora para confirmar
                            </Typography>
                          </>
                        ) : (
                          <Typography variant="body2" sx={{ fontSize: 16 }}>{row.descricao}</Typography>
                        )}
                      </StyledTableCell>
                      <StyledTableCell align="center">
                        {temAcesso('ambiente-interno', 'alteracao') ? (
                          <Select 
                            value={row.gravidade} 
                            onChange={(e) => handleChange(index, 'gravidade', e.target.value)} 
                            size="small"
                            renderValue={(selected) => String(selected)}
                          >
                            {gravidadeOptions.map((opt) => (
                              <MenuItem key={opt.value} value={opt.value}>
                                {opt.label}
                              </MenuItem>
                            ))}
                          </Select>
                        ) : (
                          <Typography variant="body2">{row.gravidade}</Typography>
                        )}
                      </StyledTableCell>
                      <StyledTableCell align="center">
                        {temAcesso('ambiente-interno', 'alteracao') ? (
                          <Select 
                            value={row.urgencia} 
                            onChange={(e) => handleChange(index, 'urgencia', e.target.value)} 
                            size="small"
                            renderValue={(selected) => String(selected)}
                          >
                            {urgenciaOptions.map((opt) => (
                              <MenuItem key={opt.value} value={opt.value}>
                                {opt.label}
                              </MenuItem>
                            ))}
                          </Select>
                        ) : (
                          <Typography variant="body2">{row.urgencia}</Typography>
                        )}
                      </StyledTableCell>
                      <StyledTableCell align="center">
                        {temAcesso('ambiente-interno', 'alteracao') ? (
                          <Select 
                            value={row.tendencia} 
                            onChange={(e) => handleChange(index, 'tendencia', e.target.value)} 
                            size="small"
                            renderValue={(selected) => String(selected)}
                          >
                            {tendenciaOptions.map((opt) => (
                              <MenuItem key={opt.value} value={opt.value}>
                                {opt.label}
                              </MenuItem>
                            ))}
                          </Select>
                        ) : (
                          <Typography variant="body2">{row.tendencia}</Typography>
                        )}
                      </StyledTableCell>
                      <StyledTableCell align="center">
                        <Typography variant="body2">{row.pontuacao}</Typography>
                      </StyledTableCell>
                      <StyledTableCell align="center">
                        <ProtectedBasicMenu
                          tela='ambiente-interno' 
                          onEdit={() => {
                            originalValueRef.current = row.descricao;
                            canBlurRef.current = false;
                            setEditingRow(index);
                          }} 
                          onDelete={() => handleDeleteRow(index)} 
                        />
                      </StyledTableCell>
                    </StyledTableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>
      </Grid>

      <ConfirmDeleteDialog
        open={openConfirmDialog}
        onClose={() => setOpenConfirmDialog(false)}
        onConfirm={handleDeleteAll}
        title="Confirmar Exclusão"
        message="Tem certeza de que deseja excluir os pontos fracos selecionados?"
        confirmButtonText="Confirmar"
      />
    </Box>
  );
};

export default PontosFracos;
